/**
 * Presets de algoritmos de cuota (SSOT S5, §8.2).
 *
 * Cada preset es un módulo versionado con identificación estable y ciclo de vida
 * `draft → pending_review → activo → obsoleto`. La UI de admin muestra los que están
 * "PENDIENTES DE IDENTIFICACIÓN/APROBACIÓN" con su explicación en español llano, y
 * nunca los oculta.
 */
import { z } from 'zod';
import { OddsBpSchema, RakeBpSchema } from '../money.js';
import { PoliticaDesbalanceSchema } from './rules.js';

export const PresetStatusSchema = z.enum(['draft', 'pending_review', 'activo', 'obsoleto']);
export type PresetStatus = z.infer<typeof PresetStatusSchema>;

/** Transiciones válidas del ciclo de vida. Cualquier otra ⇒ PRESET_INVALID_TRANSITION. */
export const PRESET_TRANSITIONS: Record<PresetStatus, readonly PresetStatus[]> = {
  draft: ['pending_review', 'obsoleto'],
  pending_review: ['activo', 'draft', 'obsoleto'],
  activo: ['obsoleto'],
  obsoleto: [],
};

export const IdentificacionEstadoSchema = z.enum(['asignada', 'pendiente']);
export type IdentificacionEstado = z.infer<typeof IdentificacionEstadoSchema>;

/** Bloque de identificación del preset (§8.2). */
export const IdentificacionSchema = z.object({
  codigoCorto: z.string().min(2).max(16).nullable(),
  uuid: z.string().uuid().nullable(),
  responsable: z.string().min(2).max(120).nullable(),
  estado: IdentificacionEstadoSchema,
});
export type Identificacion = z.infer<typeof IdentificacionSchema>;

export const PresetKindSchema = z.enum([
  'tot', // pari-mutuel totalizador (2 resultados)
  'book', // cuota fija
  'book_vig', // cuota fija con overround
  'hyb', // híbrido: semilla por mercado + pari-mutuel con piso/tope
  'lmsr', // market maker LMSR (Hanson)
  'rake_dyn', // rake dinámico por desbalance
  'kelly_cap', // complemento de control de exposición (no fija cuota)
]);
export type PresetKind = z.infer<typeof PresetKindSchema>;

export const RiskProfileSchema = z.enum(['casa_sin_riesgo_cuota', 'riesgo_medio', 'riesgo_alto']);
export type RiskProfile = z.infer<typeof RiskProfileSchema>;

/**
 * Parámetros por tipo de preset. Todos acotados con min/max (§8.2).
 * `params_schema` es el contrato de validación de la UI de admin y de la API.
 */
export const PresetParamsSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('tot'),
    rakeBp: RakeBpSchema,
    pisoBp: OddsBpSchema.or(z.literal(0)),
    topeBp: OddsBpSchema.or(z.literal(0)),
    politicaDesbalance: PoliticaDesbalanceSchema,
    houseEscrowCents: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('book'),
    overroundBp: RakeBpSchema,
    maxExposureCents: z.number().int().nonnegative(),
    pisoBp: OddsBpSchema.or(z.literal(0)),
    topeBp: OddsBpSchema.or(z.literal(0)),
  }),
  z.object({
    kind: z.literal('book_vig'),
    overroundBp: RakeBpSchema,
    maxExposureCents: z.number().int().nonnegative(),
    balanceoPorStakes: z.boolean(),
    pisoBp: OddsBpSchema.or(z.literal(0)),
    topeBp: OddsBpSchema.or(z.literal(0)),
  }),
  z.object({
    kind: z.literal('hyb'),
    rakeBp: RakeBpSchema,
    seedOddsRedBp: OddsBpSchema,
    seedOddsBlueBp: OddsBpSchema,
    pesoSeedBp: z.number().int().min(0).max(10_000),
    pisoBp: OddsBpSchema.or(z.literal(0)),
    topeBp: OddsBpSchema.or(z.literal(0)),
    politicaDesbalance: PoliticaDesbalanceSchema,
    houseEscrowCents: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('lmsr'),
    liquidezB: z.number().positive().max(1_000_000),
    seedOddsRedBp: OddsBpSchema,
    seedOddsBlueBp: OddsBpSchema,
    topeBp: OddsBpSchema.or(z.literal(0)),
  }),
  z.object({
    kind: z.literal('rake_dyn'),
    rakeMinBp: RakeBpSchema,
    rakeMaxBp: RakeBpSchema,
    umbralDesbalanceBp: z.number().int().min(0).max(10_000),
    pisoBp: OddsBpSchema.or(z.literal(0)),
    topeBp: OddsBpSchema.or(z.literal(0)),
  }),
  z.object({
    kind: z.literal('kelly_cap'),
    kellyFractionBp: z.number().int().min(0).max(10_000),
    maxStakePorApostadorCents: z.number().int().nonnegative(),
  }),
]);
export type PresetParams = z.infer<typeof PresetParamsSchema>;

export const PresetSchema = z.object({
  id: z.string().min(3).max(64),
  semver: z.string().regex(/^\d+\.\d+\.\d+$/),
  status: PresetStatusSchema,
  name: z.string().min(3).max(120),
  /** Explicación en español llano: qué hace, cuándo conviene, riesgo de la casa. */
  explanationMd: z.string().min(20),
  kind: PresetKindSchema,
  params: PresetParamsSchema,
  riskProfile: RiskProfileSchema,
  /** Datos que el preset necesita para operar (para mostrar en la ficha). */
  requisitosDatos: z.array(z.string()),
  identificacion: IdentificacionSchema,
  autor: z.string().min(2).max(120),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }).optional(),
});
export type Preset = z.infer<typeof PresetSchema>;

/** ¿El preset está listo para ser activado en una pelea? */
export function isPresetUsable(preset: Pick<Preset, 'status'>): boolean {
  return preset.status === 'activo';
}

/** ¿Necesita atención de identificación/aprobación? (badge PENDIENTE en UI) */
export function isPresetPending(preset: Pick<Preset, 'status' | 'identificacion'>): boolean {
  return preset.status !== 'activo' || preset.identificacion.estado === 'pendiente';
}

export function isValidPresetTransition(from: PresetStatus, to: PresetStatus): boolean {
  return PRESET_TRANSITIONS[from].includes(to);
}
